/**
 * Shared table-footer pagination (iter274).
 *
 * Mirrors the InvoiceTable footer pattern — "Showing X to Y of Z" on the
 * left with a page-size selector, prev/next + page indicator on the
 * right. Designed to slot under any list/table without dictating its
 * styles.
 *
 * Props (all required so the caller owns the state):
 *   - currentPage           1-indexed
 *   - totalPages            ≥ 1 (caller computes from filtered count / per-page)
 *   - itemsPerPage          current page size
 *   - totalItems            count of the filtered/visible data set
 *   - itemLabel             singular noun for the count line (e.g. "enrollment")
 *   - onPrevPage / onNextPage
 *   - onItemsPerPageChange  (Number) => void
 *   - perPageOptions        array of integers, defaults to [10, 25, 50, 100]
 */
import { Button } from '../ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function TablePaginationFooter({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  itemLabel = 'item',
  onPrevPage,
  onNextPage,
  onItemsPerPageChange,
  perPageOptions = [10, 25, 50, 100],
  testIdPrefix = 'table-pagination',
}) {
  if (!totalItems) return null;
  const startIdx = ((currentPage - 1) * itemsPerPage) + 1;
  const endIdx = Math.min(currentPage * itemsPerPage, totalItems);
  return (
    <div
      className="flex items-center justify-between px-4 py-4 border-t flex-wrap gap-3"
      data-testid={`${testIdPrefix}-footer`}
    >
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-500" data-testid={`${testIdPrefix}-count`}>
          Showing {startIdx} to {endIdx} of {totalItems} {itemLabel}
          {totalItems === 1 ? '' : 's'}
        </span>
        <Select
          value={itemsPerPage.toString()}
          onValueChange={(v) => onItemsPerPageChange(Number(v))}
        >
          <SelectTrigger
            className="w-[110px] h-8"
            data-testid={`${testIdPrefix}-page-size`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {perPageOptions.map((n) => (
              <SelectItem key={n} value={n.toString()}>
                {n} / page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevPage}
          disabled={currentPage === 1}
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft className="h-4 w-4" /> Prev
        </Button>
        <span
          className="text-sm px-2"
          data-testid={`${testIdPrefix}-page-indicator`}
        >
          Page {currentPage} of {Math.max(1, totalPages)}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onNextPage}
          disabled={currentPage >= totalPages}
          data-testid={`${testIdPrefix}-next`}
        >
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
